/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import WaveSurfer from 'wavesurfer.js';

export default class PeakDataProcessor {
  private samplerate = 16;
  private duration = 5 * 60; // 5 minutes
  private datasize = this.samplerate * this.duration;
  private dur_increment = 20000; // 4 minutes
  private peaks: number[] = Array(this.datasize).fill(0);
  private peakseek = 0;
  private lastSegmentEndTimeMs = 0;
  private onEndTimeUpdateCallback?: (endTimeMs: number) => void;
  public async updateWavesurfer(wavesurfer: WaveSurfer): Promise<void> {
    // Always use load() to ensure the waveform display is updated with new peak data
    await wavesurfer.load('', [this.peaks], this.duration);
  }

  public once = false;
  public processPeakDataSegment(segment: {
    startTimeMs: number;
    endTimeMs: number;
    peaks: number[];
  }): void {
    const { startTimeMs, endTimeMs, peaks } = segment;

    // Convert time from milliseconds to sample indices at our sample rate
    const startSample = Math.floor((startTimeMs / 1000) * this.samplerate);
    const endSample = Math.ceil((endTimeMs / 1000) * this.samplerate);
    const segmentLength = endSample - startSample;

    // Ensure our peaks array is large enough
    const requiredSize = endSample + 1;
    while (this.peaks.length < requiredSize) {
      const increment = Math.max(this.dur_increment, requiredSize - this.peaks.length);
      this.peaks = this.peaks.concat(new Array(increment).fill(0));
      this.datasize += increment;
      this.duration = this.datasize / this.samplerate;
    }

    // If we have fewer peaks than expected samples, repeat the last peak or interpolate
    let processedPeaks = [...peaks];
    if (processedPeaks.length < segmentLength) {
      // Repeat peaks to match segment length
      while (processedPeaks.length < segmentLength) {
        processedPeaks = processedPeaks.concat(peaks);
      }
      processedPeaks = processedPeaks.slice(0, segmentLength);
    } else if (processedPeaks.length > segmentLength) {
      // Truncate if we have too many peaks
      processedPeaks = processedPeaks.slice(0, segmentLength);
    }

    // Insert the peak data at the correct position
    for (let i = 0; i < processedPeaks.length && startSample + i < this.peaks.length; i++) {
      this.peaks[startSample + i] = processedPeaks[i];
    }

    // Update our peak seek position if this segment extends beyond what we've seen
    this.peakseek = Math.max(this.peakseek, endSample);

    // Track the latest segment end time for fallback when timeEnd is null
    const previousEndTime = this.lastSegmentEndTimeMs;
    this.lastSegmentEndTimeMs = Math.max(this.lastSegmentEndTimeMs, endTimeMs);

    // Notify callback if end time changed
    if (this.lastSegmentEndTimeMs !== previousEndTime && this.onEndTimeUpdateCallback) {
      this.onEndTimeUpdateCallback(this.lastSegmentEndTimeMs);
    }
  }

  /**
   * Gets the end time in milliseconds of the last processed PeakDataSegment
   * Used as fallback when Fragment timeEnd/timeEndAdjusted is null
   */
  public getLastSegmentEndTimeMs(): number {
    return this.lastSegmentEndTimeMs;
  }

  /**
   * Sets a callback to be called whenever the last segment end time is updated
   * @param callback - Function to call with new end time in milliseconds
   */
  public setOnEndTimeUpdateCallback(callback: (endTimeMs: number) => void): void {
    this.onEndTimeUpdateCallback = callback;
  }

  /**
   * Removes the end time update callback
   */
  public clearOnEndTimeUpdateCallback(): void {
    this.onEndTimeUpdateCallback = undefined;
  }
}
